/**
 * Site KPI derived values — the card figures and chart series the two panels
 * read from the shared dashboard payload.
 *
 * CR-5868: extracted verbatim from SiteKPITab.tsx (tab split into panels to
 * stay under the 500-line limit). Pure: no hooks, so it is safe to call from
 * the tab shell once and hand the result to both panels.
 */
import type { DashboardData, DashboardFilterState } from '../types'
import { firstTarget } from '../kpiTargets'
import type { DeliveryThresholds, QualityGradeBands } from '../SiteKPIComponents'
import { calculateTrendLine, getPercentageDomain } from '../dashboardUtils'
import { effectiveDateTo } from '../dashboardFilterUtils'

/**
 * Derive every figure and series the Site KPI panels render.
 *
 * @param data - the dashboard payload (with the lazy `company_kpi` slice loaded)
 * @returns card comparison values, thresholds and chart datasets
 */
export function deriveSiteKPIMetrics(data: DashboardData) {
  // Get site KPI data with defaults
  const siteKPIData = data.siteKPI || {
    otifBySite: [],
    ppmBySite: [],
    ncrBreakdown: [],
    deliveryBySite: [],
  }

  // CR-4211 / CR-5873: configurable targets from SupplierPerformanceSettings.
  // CR-7714 FE-20: no target means none, never an invented 2000.
  const ppmTarget = firstTarget(data.targets?.ppm, data.quality?.ppmTarget)

  // Three-tier color thresholds for KPI cards (green/yellow/red)
  const otifThresholds: DeliveryThresholds = data.targets?.otif_grade_bands ?? []
  // CR-6652: configurable PPM colour bands (replaces three-tier thresholds)
  const ppmGradeBands: QualityGradeBands = data.targets?.ppm_grade_bands ?? []

  // Spend values: sum from spend-by-month chart data for period accuracy
  const spendByMonthData: Array<{ month: string; spend: number }> = data.charts?.spendByMonth || []
  const spendFromChart = (months: number) => {
    const recent = spendByMonthData.slice(-months)
    return recent.reduce((sum, m) => sum + (m.spend || 0), 0)
  }
  const spendComparisonValues = {
    month: spendByMonthData.length > 0 ? spendFromChart(1) : (data.cost?.totalSpend || 0),
    threeMonth: spendByMonthData.length > 0 ? spendFromChart(3) : (data.cost?.totalSpend || 0),
    twelveMonth: spendByMonthData.length > 0 ? spendFromChart(12) : (data.cost?.totalSpend || 0),
  }

  // OTIF comparison values for side-by-side display.
  // CR-6543: pool the backend counts (Σpassed / Σdenominator) across the
  // window instead of an unweighted mean of monthly percentages — a month
  // with 3 lines must not weigh the same as a month with 3,000, and the
  // 12-month figure must agree with the Default tab's pooled ring.
  const otifTrendDataForCalc: Array<{ month: string; otif: number | null; otifCount?: number | null; denominator?: number | null }> = data.charts?.otifData || []
  // METRIC-DEFINITIONS Q13 (wave 3a): an empty denominator is N/A (null),
  // never 0; the pooled figure is Σpassed / Σdenominator over the months.
  const otifFromChart = (months: number): number | null => {
    const recent = otifTrendDataForCalc.slice(-months)
    if (recent.length === 0) return data.quality?.otif ?? null
    const passed = recent.reduce((acc, m) => acc + (m.otifCount || 0), 0)
    const denominator = recent.reduce((acc, m) => acc + (m.denominator || 0), 0)
    return denominator > 0 ? (passed / denominator) * 100 : null
  }
  const otifComparisonValues = {
    month: otifTrendDataForCalc.length > 0 ? otifFromChart(1) : (data.quality?.otif ?? null),
    threeMonth: otifTrendDataForCalc.length > 0 ? otifFromChart(3) : (data.quality?.otif ?? null),
    twelveMonth: otifTrendDataForCalc.length > 0 ? otifFromChart(12) : (data.quality?.otif ?? null),
  }

  // PPM comparison values for side-by-side display
  const ppmTrendDataForCalc: Array<{ month: string; ppm: number }> = data.charts?.ppmTrend || []
  const ppmFromChart = (months: number) => {
    const recent = ppmTrendDataForCalc.slice(-months)
    if (recent.length === 0) return data.quality?.ppm || 0
    const sum = recent.reduce((acc, m) => acc + (m.ppm || 0), 0)
    return Math.round(sum / recent.length)
  }
  const ppmComparisonValues = {
    month: ppmTrendDataForCalc.length > 0 ? ppmFromChart(1) : (data.quality?.ppm || 0),
    threeMonth: ppmTrendDataForCalc.length > 0 ? ppmFromChart(3) : (data.quality?.ppm || 0),
    twelveMonth: ppmTrendDataForCalc.length > 0 ? ppmFromChart(12) : (data.quality?.ppm || 0),
  }

  // CR-3837: Use API data for appeals breakdown instead of hardcoded values
  const appealsData = data.delivery?.appealsBreakdown || {
    raised_by_customer: 0,
    raised_by_supplier: 0,
    accepted: 0,
    rejected: 0,
    auto_approved: 0,
  }

  // OTIF trend data - targets now come from API via SupplierPerformanceSettings
  // Calculate trend line to match DefaultTab visualization
  const otifTrendData = data.charts?.otifData || []
  const otifDataWithTrend = calculateTrendLine(otifTrendData, 'otif', 'otifTrend')
  const otifDomain = getPercentageDomain(otifDataWithTrend, 'otif')

  // CR-3837: Use API data for appeals trend instead of hardcoded values
  const appealsTrendData = data.charts?.appealsTrend || []

  // Spend trend data
  const spendTrendData = data.charts?.spendByMonth || []

  // Depth of delivery data
  const depthOfDeliveryData = siteKPIData.deliveryBySite

  // Arrears by lines data — CR-6286: the Site KPI fetch (dashboard_stats) does
  // not return charts.arrearsDetails (that dataset only exists on the lazy modal
  // endpoint), so the graph was always blank. Reuse the per-month arrears
  // PO-line count already carried in otifData — the same 'arrears' series the
  // OTIF graph plots — shaped to { month, lines } for this chart.
  const arrearsByLinesData = otifTrendData.map((m) => ({ month: m.month, lines: m.arrears }))

  // Arrears by value data
  const arrearsByValueData = data.charts?.historicalArrears || []

  return {
    siteKPIData,
    ppmTarget,
    otifThresholds,
    ppmGradeBands,
    spendComparisonValues,
    otifComparisonValues,
    ppmComparisonValues,
    appealsData,
    otifDataWithTrend,
    otifDomain,
    appealsTrendData,
    spendTrendData,
    depthOfDeliveryData,
    arrearsByLinesData,
    arrearsByValueData,
  }
}

export type SiteKPIMetrics = ReturnType<typeof deriveSiteKPIMetrics>

/**
 * CR-5868: the window every Site KPI figure and drill-down was computed over,
 * so the tab states its timeframe instead of leaving the reader to infer it
 * from the (collapsed) filter panel. Reads the SAME capped end date the
 * charts and drills use (W3-T5), so the caption never disagrees with them.
 *
 * @param filters - the dashboard filter state (undefined before it loads)
 * @param formatDate - the tenant's date formatter from usePreferences
 * @returns a phrase to follow "Timeframe: "
 */
export function describeSiteKPITimeframe(
  filters: Pick<DashboardFilterState, 'dateFrom' | 'dateTo' | 'dataMode'> | undefined,
  formatDate: (iso: string) => string,
): string {
  if (!filters) return 'last 12 months'
  const from = filters.dateFrom
  const to = effectiveDateTo(filters.dateTo, filters.dataMode)
  if (from && to) return `${formatDate(from)} to ${formatDate(to)}`
  if (from) return `from ${formatDate(from)}`
  if (to) return `to ${formatDate(to)}`
  return 'all dates'
}
